import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle } from 'lucide-react';

interface LossStreakControlsProps {
  enabled: boolean;
  maxConsecutiveLosses: number;
  resumeAfterWin: boolean;
  onEnabledChange: (enabled: boolean) => void;
  onMaxLossesChange: (maxLosses: number) => void;
  onResumeAfterWinChange: (resumeAfterWin: boolean) => void;
}

export const LossStreakControls: React.FC<LossStreakControlsProps> = ({
  enabled,
  maxConsecutiveLosses,
  resumeAfterWin,
  onEnabledChange,
  onMaxLossesChange,
  onResumeAfterWinChange,
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle size={18} />
          Loss Streak Protection
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="lossStreakEnabled"
            checked={enabled}
            onCheckedChange={onEnabledChange}
          />
          <Label htmlFor="lossStreakEnabled">
            Enable loss streak pause
          </Label>
        </div>
        
        {enabled && (
          <div className="space-y-4 pl-6 border-l-2 border-muted">
            <div>
              <Label>Max consecutive losses before pause</Label>
              <Input
                type="number"
                min="1"
                max="20"
                value={maxConsecutiveLosses}
                onChange={(e) => onMaxLossesChange(parseInt(e.target.value) || 1)}
                className="w-24 mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Trading will pause after this many losses in a row
              </p>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="resumeAfterWin"
                checked={resumeAfterWin}
                onCheckedChange={onResumeAfterWinChange}
              />
              <Label htmlFor="resumeAfterWin" className="text-sm">
                Resume trading <em>after</em> winning trade (exclude the winning trade from results)
              </Label>
            </div>
            
            {!resumeAfterWin && (
              <p className="text-xs text-muted-foreground pl-6">
                ✓ Resume trading <em>on</em> winning trade (include the winning trade in results)
              </p>
            )}
            
            <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
              <strong>How it works:</strong>
              <br />
              • Trades are sorted by date/time first
              • When {maxConsecutiveLosses} losses occur in a row (based on original P&L), trading pauses
              • Trading resumes when a winning trade appears
              • Skipped trades are marked and excluded from equity/statistics
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};